import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { MatRadioChange } from '@angular/material/radio';
import { FormStatusEnum } from 'src/app/common/enumerations/enumerations';

@Component({
  selector: 'cl-checkbox-and-radio',
  templateUrl: './cl-checkbox-and-radio.component.html',
  styleUrls: ['./cl-checkbox-and-radio.component.scss']
})

export class ClCheckboxAndRadioComponent extends ClBaseComponent implements OnInit, OnChanges, OnDestroy {

  /** The task's workflow condition, used to recognize the KE form-lookup questions. */
  @Input() condition?: string | null;

  /**
   * The KE section 1 form-lookup questions (active SCFs/RSFs and their rad-survey status) keep the
   * checkbox-signs workflow: their radio answer is system-managed, set and locked by KEService as
   * forms get activated and amended, and the checkbox is the human signature, so it must stay
   * hand-tickable and unchecking it must not clear the system's answer. The backend appends 'Loc'
   * to the condition for location-only matches, so those variants are covered too.
   */
  private isKeFormLookupTask(): boolean {
    const condition = this.condition?.trim();
    return condition == '!SCFActive' || condition == '!RSFActive' ||
      condition == '!SCFActiveLoc' || condition == '!RSFActiveLoc';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.radioSelection || this.radioSelection == 0) { this.setRadio(); }
    this.checked = this.user != null;
  }

  ngOnInit() {
  }

  checkboxChanged(e: any) {
    this.checked = e.checked;
    this.user = this.checked ? this.getCurrentUser(true) : null;

    // Removing the initials removes the answer they were given for. Cleared here as well as being
    // saved by the parent, so the buttons let go straight away rather than after the round trip.
    // Zero is the empty selection: the builder rejects a radio option whose value is 0.
    // On the KE form-lookup questions the answer belongs to the system, not the signer, so it
    // stays put when the signature is removed.
    if (!this.checked && !this.isKeFormLookupTask()) {
      this.radioSelection = 0;
      this.setRadio();
    }

    // Emitted once. Clearing used to emit an extra argumentless event first, which the handler
    // could not read and which now would run the clearing branch twice.
    this.answered.emit(this.checked);
  }

  radioChange(e: MatRadioChange) {
    this.radioSelected.emit(e.value);
    this.radioSelection = e.value;
    this.checked = true;
    this.user = this.getCurrentUser(true);
    this.setRadio();
  }

  /**
   * The checkbox is only interactive to clear the signature. Picking a radio option applies the
   * initials on its own, so there is never a reason to tick the box by hand, and allowing it let a
   * user sign a task the radio group had already refused them.
   *
   * The KE form-lookup questions are the exception: there the radio is the system's answer and is
   * locked when a rad survey is required, so the checkbox is the only way to sign and stays
   * enabled once an answer exists.
   */
  checkboxEnabled() {
    if (this.isKeFormLookupTask()) {
      return !this.disabled &&
        (this.checked || (this.radioOptions?.some(r => r.checked) ?? false));
    }
    return !this.disabled && this.checked;
  }

  isNoOption(option: { label?: string | null }): boolean {
    const label = option.label?.replace(/<[^>]*>/g, '').trim().toLowerCase();
    return label === 'no';
  }

  hasCornerNoOption(): boolean {
    return this.radioOptions?.some(r => this.isNoOption(r)) ?? false;
  }

  /**
   * Identifies statuses where Rad Survey associations are historical snapshots.
   * @returns True when live status colors should no longer be shown.
   */
  isStaticRadSurveyDisplay(): boolean {
    return this.checklist?.status === FormStatusEnum.KeyEnable ||
      this.checklist?.status === FormStatusEnum.Handshake ||
      this.checklist?.status === FormStatusEnum.Closed;
  }

  setRadio() {
    this.radioOptions?.map(r => {
      r.checked = r.value == this.radioSelection;
    });
  }

  override get labelHtml(): string {
    const flattened = this.flattenNestedB(this.stripStrong(this.question) ?? '');
    return flattened.replace(/<b>/gi, '<b class="blue">');
  }
}
